import {
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { MatxLayoutSettings } from "../MatxLayout/settings";

// for removing some columns based on certain conditions
const filterColumns = (columns) =>
  columns
    .filter((column) => !column.hidden)
    .map((column) =>
      column.children
        ? {
          ...column,
          children: column.children.filter((child) => !child.hidden),
        }
        : column
    );

const TableComponent = ({
  loading,
  columns,
  rows,
  noDataMessage,
  xsScreenWidth,
  smScreenWidth,
  mdScreenWidth,
  lgScreenWidth,
  onRowClick,
  isRowSelected,
  sx = {},
}) => {
  const theme = useTheme();

  const visibleColumns = filterColumns(columns);

  return (
    <TableContainer
      component={Paper}
      sx={{
        maxHeight: "100%",
        overflowX: "auto",
        width: {
          xs: xsScreenWidth ?? "100%",
          sm: smScreenWidth ?? "100%",
          md: mdScreenWidth ?? "100%",
          lg: lgScreenWidth ?? "100%",
        },
        boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
        "&::-webkit-scrollbar": {
          width: "6px",
          height: "6px",
        },
        "&::-webkit-scrollbar-track": {
          background: "#f1f1f1",
        },
        "&::-webkit-scrollbar-thumb": {
          background: "#ccc",
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          background: "#aaa",
        },
        scrollbarWidth: "thin",
        scrollbarColor: "#ccc #f1f1f1",
        ...sx,
      }}
    >
      <Table
        sx={{ minWidth: "100%", tableLayout: "auto" }}
        aria-label="custom table"
        stickyHeader
      >
        <TableHead
          sx={{
            backgroundColor: MatxLayoutSettings.palette.active,
            position: "sticky",
            top: 0,
            zIndex: 1,
            "& .MuiTableCell-head": {
              backgroundColor: MatxLayoutSettings.palette.active,
              color: MatxLayoutSettings.palette.white,
            },
          }}
        >
          {/* First header row */}
          <TableRow sx={{ height: "auto" }}>
            {visibleColumns.map((column, index) =>
              column.children ? (
                <TableCell
                  key={index}
                  sx={{
                    textAlign: column?.alignHeader || "center",
                    fontSize: "0.875rem",
                    px: 1,
                    py: 1,
                    width: column.width || `${100 / (visibleColumns?.length + 1)}%`,
                    minWidth: column.minWidth || "auto",
                    color: MatxLayoutSettings.palette.white,
                    backgroundColor: "inherit",
                    cursor: "pointer",
                    wordWrap: "break-word",
                    wordBreak: "unset",
                    borderBottom: `1px solid ${MatxLayoutSettings.palette.white}`,
                  }}
                  colSpan={column.children.length}
                >
                  {column.headerRender ? column.headerRender() : column.label}
                </TableCell>
              ) : (
                <TableCell
                  key={index}
                  sx={{
                    textAlign: column?.alignHeader || "center",
                    fontSize: "0.875rem",
                    px: 1,
                    py: 1,
                    width: column.width || `${100 / (visibleColumns?.length + 1)}%`,
                    minWidth: column.minWidth || "auto",
                    color: MatxLayoutSettings.palette.white,
                    backgroundColor: "inherit",
                    cursor: "pointer",
                    wordWrap: "break-word",
                    wordBreak: "unset",
                    borderBottom: "none",
                  }}
                  rowSpan={2}
                >
                  {column.headerRender ? column.headerRender() : column.label}
                </TableCell>
              )
            )}
          </TableRow>

          {/* Second header row (children only) */}
          <TableRow sx={{ height: "auto" }}>
            {visibleColumns.map((column, index) =>
              column.children
                ? column.children.map((child, childIndex) => (
                  <TableCell
                    key={`${index}-${childIndex}`}
                    sx={{
                      textAlign: column?.alignHeader || "center",
                      fontSize: "0.875rem",
                      px: 1,
                      py: 1,
                      width: child.width || `${100 / (visibleColumns?.length + 1)}%`,
                      minWidth: child.minWidth || "auto",
                      color: MatxLayoutSettings.palette.white,
                      backgroundColor: "inherit",
                      cursor: "pointer",
                      wordWrap: "break-word",
                      wordBreak: "unset",
                      borderBottom: "none",
                    }}
                  >
                    {child.label}
                  </TableCell>
                ))
                : null
            )}
          </TableRow>
        </TableHead>

        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell
                colSpan={visibleColumns.length}
                align="center"
                sx={{ py: 4 }}
              >
                <CircularProgress sx={{ color: MatxLayoutSettings.palette.active }} />
              </TableCell>
            </TableRow>
          ) : rows.length > 0 ? (
            rows.map((row, index) => (
              <TableRow
                key={index}
                sx={{
                  cursor: "pointer",
                  height: "60px",
                  backgroundColor:
                    index % 2 === 0
                      ? MatxLayoutSettings.palette.tableRow
                      : "inherit",
                  "&:hover": { backgroundColor: "#f5f5f5" },
                }}
                onClick={() => onRowClick?.(row?.id)}
                selected={isRowSelected ? isRowSelected(row) : false}
              >
                {visibleColumns.map((column, colIndex) =>
                  column.children ? (
                    column.children.map((child, childIndex) => (
                      <TableCell
                        key={`${index}-${colIndex}-${childIndex}`}
                        sx={{
                          textAlign: column?.alignContents || "center",
                          fontSize: "0.8rem",
                          px: 1,
                          py: 1,
                          width: child.width || `${100 / (visibleColumns?.length + 1)}%`,
                          minWidth: child.minWidth || "auto",
                          color: theme.palette.text.primary,
                          backgroundColor: "inherit",
                          cursor: "pointer",
                          wordWrap: "break-word",
                          wordBreak: "unset",
                        }}
                      >
                        {child.render
                          ? child.render(row)
                          : row[child.field] ?? "-"}
                      </TableCell>
                    ))
                  ) : (
                    <TableCell
                      key={`${index}-${colIndex}`}
                      sx={{
                        textAlign: column?.alignContents || "center",
                        fontSize: "0.8rem",
                        px: 1,
                        py: 1,
                        width: column.width || `${100 / (visibleColumns?.length + 1)}%`,
                        minWidth: column.minWidth || "auto",
                        backgroundColor: "inherit",
                        cursor: "pointer",
                        wordWrap: "break-word",
                        wordBreak: "unset",
                        color: theme.palette.text.primary,
                      }}
                    >
                      {column?.render
                        ? column.render(row, index)
                        : row[column.field] ?? "-"}
                    </TableCell>
                  )
                )}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={visibleColumns.length}
                sx={{
                  textAlign: "center",
                  py: 4,
                  borderBottom: "1px solid rgba(224, 224, 224, 1)",
                }}
              >
                <Typography variant="body1" color="textSecondary">
                  {noDataMessage}
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TableComponent;
